import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Autocomplete,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  TextField,
  Typography,
  autocompleteClasses,
  colors,
} from '@mui/material';

import { Button } from '../../../components/core';
import {
  useLazyGetPortalUserQuery,
  useLazySearchPortalUsersQuery,
  useUpdatePortalUserMutation,
} from '../../../store/modules/merchant/api';
import { PortalUserListItem } from '../../../store/modules/merchant/types';
import { useDebounce } from '../../../hooks/useDebounce';
import { toast } from '../../../util/toast';
import { getPortalUserRoleLabel } from '../../PortalUsers/portalUserRoleCatalog';
import { PORTAL_USER_GRANT_MERCHANT_ACCESS_INTRO } from '../../PortalUsers/portalUserMerchantAccessCopy';

const PaperComponent = ({ children }: React.HTMLAttributes<HTMLElement>) => (
  <Paper
    variant="outlined"
    sx={{
      border: `1px solid ${colors.grey[400]}`,
      fontSize: '14px',
      maxHeight: '260px',
      overflow: 'auto',
      [`& .${autocompleteClasses.listbox}`]: {
        maxHeight: 'initial',
        backgroundColor: 'background.paper',
      },
    }}
  >
    {children}
  </Paper>
);

type Props = {
  isOpen: boolean;
  merchantId: string;
  onClose: () => void;
  onGranted: () => void;
};

/** Matches MMS `SearchPortalUsersQuery` max page size. */
const GRANT_SEARCH_PAGE_SIZE = 500;

/** Matches portal user list search and MMS expectations. */
const GRANT_SEARCH_MIN_LENGTH = 3;

const formatUserLabel = (user: PortalUserListItem) => {
  const name = [user.givenName, user.familyName].filter(Boolean).join(' ').trim();
  return name ? `${name} (${user.email})` : user.email;
};

const userAlreadyHasMerchantAccess = (user: PortalUserListItem) =>
  user.hasDirectAccessToMerchant === true;

export const GrantMerchantAccessDialog = ({ isOpen, merchantId, onClose, onGranted }: Props) => {
  const [searchPortalUsers, { isFetching: isSearching }] = useLazySearchPortalUsersQuery();
  const [getPortalUser, { data: selectedUserDetail, isFetching: isLoadingUserDetail }] =
    useLazyGetPortalUserQuery();
  const [updatePortalUser, { isLoading: isGranting }] = useUpdatePortalUserMutation();
  const [searchResults, setSearchResults] = useState<PortalUserListItem[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [selectedUser, setSelectedUser] = useState<PortalUserListItem | null>(null);
  const latestSearchInputRef = useRef('');

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setInputValue('');
    setSearchResults([]);
    setSelectedUser(null);
    latestSearchInputRef.current = '';
  }, [isOpen, merchantId]);

  useEffect(() => {
    if (!selectedUser) {
      return;
    }
    getPortalUser(selectedUser.userId);
  }, [getPortalUser, selectedUser]);

  const selectedUserRoles = useMemo(() => {
    if (!selectedUser || selectedUserDetail?.userId !== selectedUser.userId) {
      return [];
    }
    return selectedUserDetail.roles.map((role) => role.name);
  }, [selectedUser, selectedUserDetail]);

  const debouncedSearchUsers = useDebounce(async (searchTerm: string) => {
    const trimmed = searchTerm.trim();
    if (!trimmed || trimmed.length < GRANT_SEARCH_MIN_LENGTH) {
      setSearchResults([]);
      return;
    }

    try {
      const response = await searchPortalUsers({
        searchQuery: trimmed,
        pageNumber: 1,
        pageSize: GRANT_SEARCH_PAGE_SIZE,
        grantMerchantId: merchantId,
      }).unwrap();
      if (latestSearchInputRef.current.trim() !== searchTerm.trim()) {
        return;
      }
      setSearchResults(response.data ?? []);
    } catch {
      setSearchResults([]);
    }
  }, 300);

  const options = useMemo(() => {
    if (selectedUser && !searchResults.some((user) => user.userId === selectedUser.userId)) {
      return [selectedUser, ...searchResults];
    }
    return searchResults;
  }, [searchResults, selectedUser]);

  const grantSearchNoOptionsText = useMemo(() => {
    const trimmed = inputValue.trim();
    if (!trimmed) {
      return 'Search by name or email';
    }
    if (trimmed.length < GRANT_SEARCH_MIN_LENGTH) {
      return `Enter at least ${GRANT_SEARCH_MIN_LENGTH} characters to search`;
    }
    return 'No users found';
  }, [inputValue]);

  const handleClose = () => {
    setInputValue('');
    setSearchResults([]);
    setSelectedUser(null);
    onClose();
  };

  const handleGrant = async () => {
    if (!selectedUser) return;

    if (userAlreadyHasMerchantAccess(selectedUser)) {
      toast.error('This user already has access to this merchant.');
      return;
    }

    try {
      const detail =
        selectedUserDetail?.userId === selectedUser.userId
          ? selectedUserDetail
          : await getPortalUser(selectedUser.userId).unwrap();
      const existingMerchantIds = detail.merchants.map((merchant) => merchant.merchantId);

      if (existingMerchantIds.includes(merchantId)) {
        toast.error('This user already has access to this merchant.');
        return;
      }

      await updatePortalUser({
        userId: selectedUser.userId,
        body: { merchantIds: [...existingMerchantIds, merchantId] },
      }).unwrap();

      toast.success('Merchant access granted.');
      handleClose();
      onGranted();
    } catch (error) {
      const message =
        error && typeof error === 'object' && 'data' in error
          ? String((error as { data?: { message?: string } }).data?.message ?? '')
          : '';
      toast.error(message || 'Unable to grant merchant access. Please try again.');
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: 4 } }}
    >
      <DialogTitle>Grant merchant access</DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {PORTAL_USER_GRANT_MERCHANT_ACCESS_INTRO}
        </Typography>
        <Autocomplete
          loading={isSearching}
          options={options}
          value={selectedUser}
          inputValue={inputValue}
          onInputChange={(_event, newInputValue, reason) => {
            // Autocomplete clears the input on blur when value is null; keep the user's search text.
            if (reason === 'reset' && !selectedUser && inputValue.trim() && !newInputValue.trim()) {
              latestSearchInputRef.current = inputValue;
              return;
            }

            latestSearchInputRef.current = newInputValue;
            setInputValue(newInputValue);

            if (reason === 'input') {
              debouncedSearchUsers(newInputValue);
              return;
            }

            if (reason === 'clear') {
              setSearchResults([]);
            }
          }}
          onChange={(_event, newValue) => {
            setSelectedUser(newValue);
            if (newValue) {
              const label = formatUserLabel(newValue);
              latestSearchInputRef.current = label;
              setInputValue(label);
            }
          }}
          getOptionLabel={formatUserLabel}
          isOptionEqualToValue={(option, value) => option.userId === value.userId}
          getOptionDisabled={userAlreadyHasMerchantAccess}
          PaperComponent={PaperComponent}
          noOptionsText={grantSearchNoOptionsText}
          loadingText="Searching users..."
          filterOptions={(opts) => opts}
          renderOption={(props, option) => (
            <li {...props} key={option.userId}>
              <Typography sx={{ fontSize: '14px' }}>{formatUserLabel(option)}</Typography>
              {userAlreadyHasMerchantAccess(option) ? (
                <Typography component="span" sx={{ color: 'text.secondary', ml: 1, fontSize: 12 }}>
                  Already has access
                </Typography>
              ) : null}
            </li>
          )}
          renderInput={(params) => (
            <TextField {...params} label="Portal user" placeholder="Search by name or email" />
          )}
        />
        {selectedUser ? (
          <Stack spacing={1} sx={{ mt: 2 }}>
            <Typography variant="body2" fontWeight={600}>
              Roles
            </Typography>
            {isLoadingUserDetail && selectedUserRoles.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Loading roles...
              </Typography>
            ) : selectedUserRoles.length > 0 ? (
              <Stack direction="row" flexWrap="wrap" gap={1}>
                {selectedUserRoles.map((roleName) => (
                  <Chip
                    key={roleName}
                    label={getPortalUserRoleLabel(roleName)}
                    size="small"
                    variant="outlined"
                  />
                ))}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No roles assigned
              </Typography>
            )}
          </Stack>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="text" text="Cancel" onClick={handleClose} disabled={isGranting} />
        <Button
          text="Grant access"
          onClick={handleGrant}
          disabled={!selectedUser || isGranting || userAlreadyHasMerchantAccess(selectedUser)}
          loading={isGranting}
        />
      </DialogActions>
    </Dialog>
  );
};
